import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { isEmpty, some } from 'lodash'

import ScanTicketGif from '../assets/gifs/ticket.gif'


import styles from './ScanScreen.module.scss'
import NetworkIndicator from '../containers/NetworkIndicator'
import BackButtonChevron from '../components/BackButtonChevron'
import GratuityHeader from '../components/GratuityHeader'
import SegmentedControlV2 from '../components/segmentedControl/SegmentedControl'
import { KEY_CODES } from '../kiosk/utils/constants'
import useCreateOrderInProgress from '../hooks/useCreateOrderInProgress'
import useGetTicketBalance from '../hooks/useGetTicketBalance'
import { getOrderInProgress } from '../selectors/order'
import ConnectingIndicator from '../components/ConnectingIndicator'
import { centsToDollars, getTicketIdFromResult } from '../utils/formatters'
import { addPaymentToOrderInProgress, clearOrderInProgress, closeNoOpTender } from '../actions/order'
import { toast } from 'react-toastify'
import PeripheralBridge from '../utils/peripheralBridge'

const ticketTenderChoices = [
  { label: 'Scan Ticket', value: 'scan' },
  { label: 'Type Ticket', value: 'entry' },
]

const TicketTender = ({
  className,
  onGoBack,
  needCreateOrder,
  order,
  addPaymentToOrderInProgress,
  balanceDueInCents,
  amountInCents,
  placeFreeOrder,
  push,
}) => {
  const classNames = cn(styles.scanScreen, className)
  const isMobile = window.matchMedia('(max-width: 700px)')?.matches
  const [selectedSubmissionType, setSelectedSubmissionType] = useState(isMobile ? 'entry' : 'scan')
  const [isProcessing, setIsProcessing] = useState(false)
  const [ticketId, setTicketId] = useState('')
  const [createOrder, orderCreated] = useCreateOrderInProgress()
  const [getTicketBalance, { ticketBalance, directAuthEnabled, dataSucceeded, dataFailed }] = useGetTicketBalance()
  const [componentMounted, setComponentMounted] = useState(false)
  const [keyboardIsActive, setKeyboardIsActive] = useState(false)
  const [ticketScanError, setTicketScanError] = useState(false)

  const ticketInputRef = useRef()
const orderStillHasRemainingBalance =
  balanceDueInCents && balanceDueInCents !== amountInCents

  const closeKeyBoard = () => {
    if (keyboardIsActive) {
      ticketInputRef?.current?.blur()
    }
  }

  const handleFocusChange = () => {
    setTimeout(() => {
      setKeyboardIsActive(document.activeElement === ticketInputRef?.current)
    }, 150)
  }

  const ticketAlreadyUsed = (newTicketId) => some(order?.payments, (payment) => payment?.ticketId == newTicketId)

  useEffect(() => {
    if (needCreateOrder) {
      createOrder()
    }
    setComponentMounted(true)

    document.addEventListener('focusin', handleFocusChange)
    document.addEventListener('focusout', handleFocusChange)

    return () => {
      document.removeEventListener('focusin', handleFocusChange)
      document.removeEventListener('focusout', handleFocusChange)
      const bridgeInstance = PeripheralBridge.getBridge()
      if (!bridgeInstance) return
      bridgeInstance.call('stopScanner')
    }
  }, [])

  useEffect(() => {
    if (dataFailed) {
      setIsProcessing(false)
    }
  }, [dataFailed])

  useEffect(() => {
    if (orderCreated && order?.amountInCents === 0) {
      placeFreeOrder()
    }
  }, [orderCreated])

  useEffect(() => {
    if (dataSucceeded) {
      let tenderAmountInCents
      let remainingTicketBalance
      if (directAuthEnabled) {
        tenderAmountInCents = balanceDueInCents
      } else
      if (+ticketBalance === 0) {
        setTicketId('')
        setIsProcessing(false)
        toast.error('No funds remain on this ticket. Please scan another ticket.')
        return 
      } else
      if (ticketBalance >= balanceDueInCents) {
        tenderAmountInCents = balanceDueInCents
        remainingTicketBalance = ticketBalance - balanceDueInCents
      } else {
        tenderAmountInCents = ticketBalance
        remainingTicketBalance = 0
      }
      addPaymentToOrderInProgress({
        tenderAmountInCents,
        paymentType: 'loaded_ticket',
        tenderDisplayName: 'Ticket',
        token: ticketId,
        remainingTicketBalance,
        directAuthEnabled,
      })
      setTicketId('')
      setIsProcessing(false)
    }
  }, [dataSucceeded])

  useEffect(() => {
    if (needCreateOrder || !componentMounted) return

    if (!balanceDueInCents) {
      closeKeyBoard()
      setIsProcessing(true)
    } else if (orderStillHasRemainingBalance) {
      toast.success(
        `Ticket ${selectedSubmissionType} successful. Please ${
          selectedSubmissionType === 'scan' ? 'scan' : 'input'
        } another ticket to pay remaining balance`
      )
      if (selectedSubmissionType === 'entry') {
        ticketInputRef.current?.focus()
      }
    }
  }, [balanceDueInCents])

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (selectedSubmissionType === 'entry' && ticketInputRef.current) {
      ticketInputRef.current?.focus()
    }

    if (selectedSubmissionType === 'scan') {
      if (!bridgeInstance) return
      bridgeInstance.registerHandler('scanPayload', function (data, callback) {
        const scannedTicket = data?.valueOf()
        if (ticketAlreadyUsed(scannedTicket)) {
          toast.error('Ticket already scanned and applied to order')
        } else {
          toast.success(`Ticket Scan Successful`)
          setTicketId(scannedTicket)
          getTicketBalance(scannedTicket)
          setIsProcessing(true)
          setTicketScanError(false)
        }
      })

      bridgeInstance.registerHandler('scanError', function (err, callback) {
        if (err?.toLowerCase()?.includes('no scanner detected')) {
          setSelectedSubmissionType('entry')
        } else {
          setTicketScanError(true)
        }
        toast.error(`Ticket Scan Error, ERROR: ${JSON.stringify(err)}`)
      })
    }
  }, [selectedSubmissionType, order])

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    if (isProcessing || selectedSubmissionType === 'entry') {
      bridgeInstance.call('stopScanner')
    } else if (selectedSubmissionType === 'scan') {
      bridgeInstance.call('startScanner')
    }
  }, [isProcessing, selectedSubmissionType])

  const primaryText = centsToDollars(balanceDueInCents)
  const secondaryText =
    selectedSubmissionType === 'scan'
      ? 'Please scan ticket QR code or barcode.'
      : 'Please type ticket number'
  const extraText =
    !!orderStillHasRemainingBalance &&
    `Total: ${centsToDollars(
      amountInCents
    )} • Balance Paid: ${centsToDollars(
      amountInCents - balanceDueInCents
    )}`

  const handleSubmissionTypeChange = value => {
    if (selectedSubmissionType !== value) {
      setSelectedSubmissionType(value)
    }
  }

  const handleKeyPress = e => {
    if (e.keyCode === KEY_CODES.ENTER) {
      const isValid = /^[a-z|0-9\-]*?$/gi.test(ticketId);
      if(isValid){
        if (ticketAlreadyUsed(ticketId)) {
          toast.error('Ticket already entered and applied to order')
        } else {
          getTicketBalance(ticketId)
          setIsProcessing(true)
        }
      } else {
        toast.error('The ticket ID contains invalid characters')
      }
    }
  }

  const handleTicketCodeChange = e => {
    setTicketId(e?.target?.value)
  }

  if (!orderCreated && needCreateOrder) {
    return <ConnectingIndicator />
  }

  const tenderOptionButtonText = ticketScanError ? 'Pay With Other Method' : "I Don't Have Another Ticket"
  const showTenderOptionButton =
  !isProcessing &&
  !keyboardIsActive &&
  (orderStillHasRemainingBalance || ticketBalance === 0 || ticketScanError)

  const chooseTenderOptionButton = showTenderOptionButton && (
      <div
        className={styles.chooseTenderOption}
        onClick={() => {
          push({
            pathname: '/tender-selection',
            state: {
              tenderAmountInCents: balanceDueInCents,
              scanScreenType: 'ticket'
            }
          })
        }
        }
      >
        {tenderOptionButtonText}
      </div>
    )

  return (
    <div className={classNames} >
      <div className={styles.goBack}>
        <BackButtonChevron
          onClick={() => {
            closeKeyBoard()
            setTimeout(() => {
              onGoBack(isEmpty(order?.payments))
            }, 200)
            }
          }
          backButtonText={isMobile ? '' : 'Go Back'}
        />
        <span className={styles.mobileTitle}>{primaryText}</span>
        <div className={styles.contentRight}>
          <NetworkIndicator />
        </div>
      </div>
      <div className={styles.scanScreen__header}>
        <GratuityHeader
          className={styles.gratuityHeader}
          primaryText={isMobile ? '' : primaryText}
          secondaryText={secondaryText}
          extraText={extraText}
        />
      </div>
      {!isMobile && <div className={styles.scanScreen__submissionChoices}>
        <SegmentedControlV2
          className={styles.segmentedControl}
          items={ticketTenderChoices}
          selectedValue={selectedSubmissionType}
          onSelect={handleSubmissionTypeChange}
        />
      </div>}
      <div className={styles.scanScreen__body}>
        <div className={styles.choicesContent}>
          <div
            className={cn(styles.option, {
              [styles.isHidden]:
                selectedSubmissionType === 'entry' || isProcessing
            })}
          >
            <img src={ScanTicketGif} alt={"Scan Ticket"} className={styles.qrImage}/>
          </div>
          <div
            className={cn(styles.option, {
              [styles.isHidden]:
                selectedSubmissionType === 'scan' || isProcessing
            })}
          >
            <input
              className={styles.manualEntry}
              ref={ticketInputRef}
              value={ticketId}
              onChange={handleTicketCodeChange}
              onKeyDown={handleKeyPress}
            />
          </div>
          <div
            className={cn(styles.processing, {
              [styles.isHidden]: !isProcessing
            })}
          >
            <div className={styles.loadingSpinner}>
              <ConnectingIndicator position={'initial'}/>
            </div>
            <span className={styles.topText}>Processing...</span>
            <span className={styles.bottomText}>please wait.</span>
          </div>
        </div>
        {chooseTenderOptionButton}
      </div>
    </div>
  )
}

const mapStateToProps = (state, props) => {
  const order = getOrderInProgress(state)
  const balanceDueInCents = order?.balanceDueInCents
  const amountInCents = order?.amountInCents
  const push = props?.history?.push ?? (() => {})

  return {
    order,
    needCreateOrder: isEmpty(order),
    amountInCents,
    balanceDueInCents,
    push,
  }
}

const mapDispatchToProps = (dispatch, props) => {
  const goBack = props?.history?.goBack ?? (() => {})

  return {
    onGoBack: (noPaymentsOnOrder) => {
      if (noPaymentsOnOrder) {
        dispatch(clearOrderInProgress())
      }
      goBack()
    },
    addPaymentToOrderInProgress: payment =>
      dispatch(addPaymentToOrderInProgress(payment)),
      placeFreeOrder: () => {
        dispatch(closeNoOpTender())
      }, 
  }
}
export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TicketTender)
)

